import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import indService from '../services/ind.service';

const byOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

/** Table of per-document section progress from GET /status/:projectId. */
const INDPackageStatus = ({ projectId, catalogueDocuments }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await indService.getStatus(projectId);
      setRows(Array.isArray(data?.documents) ? data.documents : []);
    } catch (err) {
      console.error('INDPackageStatus: failed to load status', err);
      setError(err.message || 'Failed to load IND package status');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const catalogue = Array.isArray(catalogueDocuments) ? [...catalogueDocuments].sort(byOrder) : [];
  const statusByKey = new Map(rows.map((r) => [r.documentKey, r]));
  // Show every catalogue document, even ones with no status row yet; append any
  // status row the catalogue does not know about so nothing is hidden.
  const displayRows = [
    ...catalogue.map((doc) => ({ doc, status: statusByKey.get(doc.key) || null })),
    ...rows
      .filter((r) => !catalogue.some((doc) => doc.key === r.documentKey))
      .map((r) => ({ doc: { key: r.documentKey, title: r.documentKey }, status: r })),
  ];

  const overallChip = (status, total) => {
    if (!status || !status.created) return <Chip size="small" label="Not started" />;
    if (total > 0 && status.final >= total) return <Chip size="small" color="success" label="Final" />;
    if (status.generated > 0) return <Chip size="small" color="primary" label="In progress" />;
    return <Chip size="small" color="default" variant="outlined" label="Initialized" />;
  };

  if (!projectId) {
    return <Alert severity="info">Select a study to see its IND package status.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6">IND Package Status</Typography>
          <Typography variant="body2" color="text.secondary">
            Section progress per authored document, with its CTD location and regulatory basis.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>CTD location</TableCell>
              <TableCell>Basis</TableCell>
              <TableCell align="right">Sections created</TableCell>
              <TableCell align="right">Generated</TableCell>
              <TableCell align="right">Final</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : displayRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No IND documents found.
                </TableCell>
              </TableRow>
            ) : (
              displayRows.map(({ doc, status }) => {
                const total = status?.total ?? (Array.isArray(doc.sections) ? doc.sections.length : 0);
                return (
                  <TableRow key={doc.key} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{doc.title}</Typography>
                      {doc.shortTitle && (
                        <Typography variant="caption" color="text.secondary">{doc.shortTitle}</Typography>
                      )}
                    </TableCell>
                    <TableCell>{doc.ctdLocation || '-'}</TableCell>
                    <TableCell>
                      {Array.isArray(doc.basis) && doc.basis.length > 0 ? doc.basis.join('; ') : '-'}
                    </TableCell>
                    <TableCell align="right">{status ? `${status.created ?? 0} / ${total}` : `0 / ${total}`}</TableCell>
                    <TableCell align="right">{status?.generated ?? 0}</TableCell>
                    <TableCell align="right">{status?.final ?? 0}</TableCell>
                    <TableCell>{overallChip(status, total)}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

INDPackageStatus.propTypes = {
  projectId: PropTypes.string,
  catalogueDocuments: PropTypes.arrayOf(PropTypes.object),
};

export default INDPackageStatus;
